import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, CheckCircle, AlertCircle, X, Info, CheckSquare, Scissors, AlertTriangle } from 'lucide-react';
import { uploadPOD } from '../services/lambdaService';
import { processPDF, getPDFPageCount } from '../services/pdfService';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 0.4fr 1fr;
  gap: 40px;
  align-items: start;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const HeroSection = styled(motion.div)`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 48px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const UploadCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  text-align: center;
  border: 1px solid #971b2f;
`;

const Dropzone = styled.div`
  border: 3px dashed ${props => props.isDragActive ? '#d4af37' : '#e0e0e0'};
  border-radius: 16px;
  padding: 60px 40px;
  background: ${props => props.isDragActive ? 'rgba(212, 175, 55, 0.05)' : '#fafafa'};
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 30px;

  &:hover {
    border-color: #d4af37;
    background: rgba(212, 175, 55, 0.05);
  }
`;

const UploadIcon = styled(Upload)`
  width: 64px;
  height: 64px;
  color: #d4af37;
  margin-bottom: 20px;
`;

const DropzoneText = styled.div`
  font-size: 18px;
  color: #666;
  margin-bottom: 8px;
`;

const DropzoneSubtext = styled.div`
  font-size: 14px;
  color: #999;
`;

const FileList = styled.div`
  margin-top: 30px;
`;

const FileItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 12px;
  border-left: 4px solid ${props => {
    if (props.status === 'success') return '#28a745';
    if (props.status === 'error') return '#dc3545';
    if (props.status === 'uploading') return '#d4af37';
    return '#e0e0e0';
  }};
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FileIcon = styled(FileText)`
  width: 24px;
  height: 24px;
  color: #666;
`;

const FileDetails = styled.div`
  text-align: left;
`;

const FileName = styled.div`
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: #999;
`;

const FileStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const ProgressBar = styled.div`
  width: 100px;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  margin-left: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(220, 53, 69, 0.1);
  }
`;

const UploadButton = styled(motion.button)`
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  transition: all 0.3s ease;
  margin-top: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  padding: 30px;
  border: 1px solid #971b2f;
  height: fit-content;
  position: sticky;
  top: 20px;
`;

const InfoTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoSection = styled.div`
  margin-bottom: 20px;
`;

const InfoSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const InfoIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoIconInner = styled(CheckSquare)`
  width: 12px;
  height: 12px;
  color: white;
`;

const SplitInfo = styled.div`
  background: linear-gradient(135deg, rgba(151, 27, 47, 0.1) 0%, rgba(151, 27, 47, 0.05) 100%);
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  border-left: 4px solid #971b2f;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SplitIcon = styled(Scissors)`
  width: 20px;
  height: 20px;
  color: #971b2f;
  flex-shrink: 0;
`;

const SplitText = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const SplitDetails = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const ProcessingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666;
  margin-top: 8px;
`;

const ProcessingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #e0e0e0;
  border-top: 2px solid #d4af37;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorNotification = styled(motion.div)`
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorIcon = styled(AlertTriangle)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const UploadPODs = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(new Set());
  const [fileSizeError, setFileSizeError] = useState(null);

  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const onDrop = useCallback(async (acceptedFiles) => {
    const processedFiles = [];
    
    for (const file of acceptedFiles) {
      const fileId = Math.random().toString(36).substr(2, 9);
      
      setProcessingFiles(prev => new Set(prev).add(fileId));
      
      try {
        const pageCount = await getPDFPageCount(file);
        const processedPdfs = await processPDF(file);
        
        const fileObjects = processedPdfs.map((pdfFile, index) => ({
          file: pdfFile,
          id: index === 0 ? fileId : `${fileId}_${index}`,
          name: pdfFile.name,
          size: pdfFile.size,
          status: 'pending',
          progress: 0,
          originalFile: file.name,
          pageCount: pageCount,
          isSplit: pageCount > 1,
          splitIndex: index + 1,
          totalSplits: processedPdfs.length
        }));
        
        processedFiles.push(...fileObjects);
      } catch (error) {
        console.error('Error processing PDF:', error);
        processedFiles.push({
          file,
          id: fileId,
          name: file.name,
          size: file.size,
          status: 'error',
          progress: 0,
          error: 'Failed to process PDF'
        });
      } finally {
        setProcessingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }
    }
    
    setFiles(prev => [...prev, ...processedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-too-large') {
            setFileSizeError(`File "${file.name}" is too large. Maximum size is 20MB.`);
            setTimeout(() => setFileSizeError(null), 5000);
          } else if (error.code === 'file-invalid-type') {
            setFileSizeError(`File "${file.name}" is not a valid PDF file.`);
            setTimeout(() => setFileSizeError(null), 5000);
          }
        });
      });
    }
  });

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    const uploadPromises = files.map(async (fileObj) => {
      try {
        setFiles(prev => prev.map(f =>
          f.id === fileObj.id ? { ...f, status: 'uploading', progress: 0 } : f
        ));

        const progressInterval = setInterval(() => {
          setFiles(prev => prev.map(f => {
            if (f.id === fileObj.id && f.progress < 90) {
              return { ...f, progress: f.progress + 10 };
            }
            return f;
          }));
        }, 200);

        const result = await uploadPOD(fileObj.file, (progress) => {
          setFiles(prev => prev.map(f =>
            f.id === fileObj.id ? { ...f, progress } : f
          ));
        });

        clearInterval(progressInterval);
        setFiles(prev => prev.map(f =>
          f.id === fileObj.id ? { ...f, status: 'success', progress: 100 } : f
        ));

        return result;
      } catch (error) {
        setFiles(prev => prev.map(f =>
          f.id === fileObj.id ? { ...f, status: 'error' } : f
        ));
        throw error;
      }
    });

    try {
      await Promise.all(uploadPromises);
      setTimeout(() => {
        setFiles([]);
      }, 3000);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container>
      <HeroSection
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Title>Upload POD Documents</Title>
        <Subtitle>
          Upload your POD documents by dragging and dropping PDF files. Multi-page PDFs will be automatically split into separate files.
        </Subtitle>
      </HeroSection>

      <ContentWrapper>
        <InfoCard
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <InfoTitle>
            <Info size={24} />
            How to Use This Service
          </InfoTitle>
          <InfoSection>
            <InfoSectionTitle>
              <Upload size={20} />
              Upload Process
            </InfoSectionTitle>
            <InfoList>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Multi-page PDFs will be automatically split into separate single-page files.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Click or drag and drop your PDF files into the dropzone.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                The system will process and split your files automatically.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Click "Upload PODs" to initiate the upload.
              </InfoListItem>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                Once the files are uploaded, they will disappear from the list.
              </InfoListItem>
            </InfoList>
          </InfoSection>
          <InfoSection>
            <InfoSectionTitle>
              <AlertTriangle size={16} />
              Warning
            </InfoSectionTitle>
            <InfoList>
              <InfoListItem>
                <InfoIcon>
                  <InfoIconInner />
                </InfoIcon>
                  The PDF size limit is 20MB.
              </InfoListItem>
            </InfoList>
          </InfoSection>
        </InfoCard>

        <UploadCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Dropzone {...getRootProps()} isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <UploadIcon />
            <DropzoneText>
              {isDragActive ? 'Drop the files here...' : 'Drag & drop PDF files here'}
            </DropzoneText>
            <DropzoneSubtext>or click to select files</DropzoneSubtext>
          </Dropzone>

          <AnimatePresence>
            {processingFiles.size > 0 && (
              <ProcessingStatus>
                <ProcessingSpinner />
                Processing PDF files...
              </ProcessingStatus>
            )}
            {fileSizeError && (
              <ErrorNotification
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ErrorContent>
                  <ErrorIcon />
                  {fileSizeError}
                </ErrorContent>
                <CloseButton onClick={() => setFileSizeError(null)}>
                  <X size={16} />
                </CloseButton>
              </ErrorNotification>
            )}
            {files.length > 0 && (
              <FileList>
                {files.map((fileObj) => (
                  <FileItem
                    key={fileObj.id}
                    status={fileObj.status}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <FileInfo>
                      <FileIcon />
                      <FileDetails>
                        <FileName>{fileObj.name}</FileName>
                        <FileSize>{formatFileSize(fileObj.size)}</FileSize>
                        {fileObj.isSplit && (
                          <SplitInfo>
                            <SplitIcon />
                            <div>
                              <SplitText>Split from {fileObj.originalFile}</SplitText>
                              <SplitDetails>Page {fileObj.splitIndex} of {fileObj.totalSplits}</SplitDetails>
                            </div>
                          </SplitInfo>
                        )}
                      </FileDetails>
                    </FileInfo>

                    <FileStatus>
                      {fileObj.status === 'pending' && 'Ready to upload'}
                      {fileObj.status === 'uploading' && (
                        <>
                          Uploading POD via Lambda...
                          <ProgressBar>
                            <ProgressFill progress={fileObj.progress} />
                          </ProgressBar>
                        </>
                      )}
                      {fileObj.status === 'success' && (
                        <>
                          <CheckCircle size={16} color="#28a745" />
                          POD Uploaded
                        </>
                      )}
                      {fileObj.status === 'error' && (
                        <>
                          <AlertCircle size={16} color="#dc3545" />
                          Failed
                        </>
                      )}
                    </FileStatus>

                    {fileObj.status === 'pending' && (
                      <RemoveButton onClick={() => removeFile(fileObj.id)}>
                        <X size={16} />
                      </RemoveButton>
                    )}
                  </FileItem>
                ))}
              </FileList>
            )}
          </AnimatePresence>

          {files.length > 0 && (
            <UploadButton
              onClick={uploadFiles}
              disabled={uploading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {uploading ? 'Uploading PODs...' : `Upload ${files.length} POD${files.length > 1 ? 's' : ''} via Lambda`}
            </UploadButton>
          )}
        </UploadCard>
      </ContentWrapper>
    </Container>
  );
};

export default UploadPODs;